/**
 * OnboardingWizard Component
 *
 * A variant-aware guided onboarding flow for new users.
 * Steps branch based on persona's onboardingVariant:
 *   - individual: welcome, persona, connect, select, analyzing, insights
 *   - team: welcome, persona, create-org, invite-team, ready
 *   - enterprise: welcome, persona, enterprise-context, ready
 *   - developer: welcome, persona, connect, ready
 *
 * Features:
 * - Modal overlay with glassmorphism design
 * - Progress stepper showing current step
 * - Skip option that advances to exploring stage
 * - Keyboard navigation (Tab, Enter, Escape)
 * - Mobile responsive
 *
 * @module components/onboarding/OnboardingWizard
 */

import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  Github,
  Sparkles,
  Check,
  ChevronRight,
  ChevronLeft,
  X,
  Zap,
  Target,
  TrendingUp,
  Users,
  Shield,
  GitBranch,
  Loader2,
  CheckCircle2,
  BarChart3,
  User,
  Building2,
  Mail,
  ArrowRight,
  Clipboard,
} from 'lucide-react';
import Button from '../ui/Button';
import { useAnalytics } from '../../services/analytics';
import PersonaSelectionStep from './PersonaSelectionStep';
import usePersona from '../../hooks/usePersona';
import usePersonaInference from '../../hooks/usePersonaInference';
import { getPersonaConfig, PERSONA_TYPES } from '../../config/personas';
import { createOrganization } from '../../services/organizationsApi';
import { getToken as getGitHubToken } from '../../services/tokenManager';

/**
 * Variant-aware step definitions
 */
const VARIANT_STEPS = {
  individual: [
    { id: 'welcome', title: 'Welcome', icon: Sparkles },
    { id: 'connect', title: 'Connect', icon: Github },
    { id: 'persona', title: 'You', icon: User },
    { id: 'select', title: 'Select', icon: GitBranch },
    { id: 'analyzing', title: 'Analyze', icon: Zap },
    { id: 'insights', title: 'Insights', icon: BarChart3 },
  ],
  team: [
    { id: 'welcome', title: 'Welcome', icon: Sparkles },
    { id: 'persona', title: 'You', icon: User },
    { id: 'create-org', title: 'Workspace', icon: Building2 },
    { id: 'invite-team', title: 'Team', icon: Users },
    { id: 'ready', title: 'Ready', icon: CheckCircle2 },
  ],
  enterprise: [
    { id: 'welcome', title: 'Welcome', icon: Sparkles },
    { id: 'persona', title: 'You', icon: User },
    { id: 'enterprise-context', title: 'Setup', icon: Building2 },
    { id: 'ready', title: 'Ready', icon: CheckCircle2 },
  ],
  developer: [
    { id: 'welcome', title: 'Welcome', icon: Sparkles },
    { id: 'persona', title: 'You', icon: User },
    { id: 'connect', title: 'Connect', icon: Github },
    { id: 'ready', title: 'Ready', icon: CheckCircle2 },
  ],
};

/**
 * Persona-specific guidance shown in the Welcome step and ReadyStep
 */
const PERSONA_GUIDANCE = {
  [PERSONA_TYPES.PROFESSIONAL]: {
    tagline: 'Build a verified portfolio from your code.',
    valueProp: 'Get AI-powered career insights, skill gap analysis, and a dynamic CV.',
  },
  [PERSONA_TYPES.FOUNDER]: {
    tagline: 'Tell your technical story to investors.',
    valueProp: 'Generate investor-ready due diligence packages backed by code evidence.',
  },
  [PERSONA_TYPES.ARCHITECT]: {
    tagline: 'Data-driven build vs buy decisions.',
    valueProp: 'Evaluate acquisition targets with technical fit scores and architecture analysis.',
  },
  [PERSONA_TYPES.ANALYST]: {
    tagline: 'Know what you are buying.',
    valueProp: 'Multi-methodology valuation of technical assets with IP and risk scoring.',
  },
  [PERSONA_TYPES.INVESTOR]: {
    tagline: 'Technical due diligence at scale.',
    valueProp: 'Assess team capability and tech stack risk without scheduling calls.',
  },
  [PERSONA_TYPES.PRODUCT_OWNER]: {
    tagline: 'Map team capabilities to your roadmap.',
    valueProp: 'Skill coverage maps and feature-to-capability matching for your team.',
  },
  [PERSONA_TYPES.DEVELOPER]: {
    tagline: 'Portfolio intelligence via API.',
    valueProp: 'Integrate skill analysis and project valuation into your own tools.',
  },
};

/**
 * Progress Stepper Component
 */
const ProgressStepper = ({ currentStep, steps }) => (
  <div className="flex items-center justify-center gap-2 mb-8">
    {steps.map((step, index) => {
      const isCompleted = index < currentStep;
      const isCurrent = index === currentStep;
      const Icon = step.icon;

      return (
        <React.Fragment key={step.id}>
          <div
            className={`flex items-center justify-center w-10 h-10 rounded-full border-2 transition-all duration-300 ${
              isCompleted
                ? 'bg-success-500 border-success-500'
                : isCurrent
                ? 'bg-primary-500/20 border-primary-500'
                : 'bg-white/5 border-white/20'
            }`}
            aria-current={isCurrent ? 'step' : undefined}
          >
            {isCompleted ? (
              <Check className="w-5 h-5 text-white" />
            ) : (
              <Icon
                className={`w-5 h-5 ${
                  isCurrent ? 'text-primary-400' : 'text-gray-500'
                }`}
              />
            )}
          </div>
          {index < steps.length - 1 && (
            <div
              className={`w-8 h-0.5 transition-colors duration-300 ${
                isCompleted ? 'bg-success-500' : 'bg-white/20'
              }`}
            />
          )}
        </React.Fragment>
      );
    })}
  </div>
);

ProgressStepper.propTypes = {
  currentStep: PropTypes.number.isRequired,
  steps: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      title: PropTypes.string.isRequired,
      icon: PropTypes.elementType.isRequired,
    })
  ).isRequired,
};

/**
 * Step: Welcome Screen
 */
const WelcomeStep = ({ onNext, onSkip, personaGuidance }) => (
  <div className="text-center">
    <div className="mb-6">
      <div className="inline-flex p-4 rounded-full bg-primary-500/20 mb-4">
        <Sparkles className="w-12 h-12 text-primary-400" />
      </div>
      <h2 className="text-2xl font-bold text-white mb-2">
        Welcome to Portfolistic
      </h2>
      <p className="text-gray-400 max-w-md mx-auto">
        {personaGuidance
          ? personaGuidance.tagline
          : "Transform your code into career intelligence. Let's get you set up in just a few minutes."}
      </p>
      {personaGuidance && (
        <p className="text-gray-500 text-sm mt-2 max-w-md mx-auto">
          {personaGuidance.valueProp}
        </p>
      )}
    </div>

    {/* Value Props */}
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
      <div className="p-4 bg-white/5 rounded-lg border border-white/10">
        <Target className="w-6 h-6 text-secondary-400 mx-auto mb-2" />
        <p className="text-sm text-gray-300">Discover your unique skills</p>
      </div>
      <div className="p-4 bg-white/5 rounded-lg border border-white/10">
        <TrendingUp className="w-6 h-6 text-success-400 mx-auto mb-2" />
        <p className="text-sm text-gray-300">Track career growth</p>
      </div>
      <div className="p-4 bg-white/5 rounded-lg border border-white/10">
        <Users className="w-6 h-6 text-primary-400 mx-auto mb-2" />
        <p className="text-sm text-gray-300">Stand out to employers</p>
      </div>
    </div>

    <div className="flex flex-col sm:flex-row gap-3 justify-center">
      <Button variant="gradient" size="lg" onClick={onNext} icon={ChevronRight} iconPosition="right">
        Get Started
      </Button>
      <Button variant="ghost" size="lg" onClick={onSkip}>
        Skip for now
      </Button>
    </div>
  </div>
);

WelcomeStep.propTypes = {
  onNext: PropTypes.func.isRequired,
  onSkip: PropTypes.func.isRequired,
  personaGuidance: PropTypes.shape({
    tagline: PropTypes.string,
    valueProp: PropTypes.string,
  }),
};

/**
 * Step: Connect GitHub
 */
const ConnectStep = ({ onNext, onBack, onSkip, hasGitHubToken, onConnectGitHub }) => (
  <div className="text-center">
    <div className="mb-6">
      <div className="inline-flex p-4 rounded-full bg-white/10 mb-4">
        <Github className="w-12 h-12 text-white" />
      </div>
      <h2 className="text-2xl font-bold text-white mb-2">
        Connect Your GitHub
      </h2>
      <p className="text-gray-400 max-w-md mx-auto">
        We'll analyze your repositories to build your portfolio intelligence.
      </p>
    </div>

    {/* Permission Info */}
    <div className="bg-white/5 rounded-lg border border-white/10 p-4 mb-6 text-left max-w-md mx-auto">
      <h3 className="text-sm font-medium text-white mb-3 flex items-center gap-2">
        <Shield className="w-4 h-4 text-success-400" />
        What we access:
      </h3>
      <ul className="space-y-2 text-sm text-gray-400">
        <li className="flex items-center gap-2">
          <Check className="w-4 h-4 text-success-400 flex-shrink-0" />
          Your public repositories
        </li>
        <li className="flex items-center gap-2">
          <Check className="w-4 h-4 text-success-400 flex-shrink-0" />
          Private repos you choose to share
        </li>
        <li className="flex items-center gap-2">
          <Check className="w-4 h-4 text-success-400 flex-shrink-0" />
          Workspace membership (read-only)
        </li>
      </ul>
      <p className="mt-3 text-xs text-gray-500">
        We never modify your code or push commits.
      </p>
    </div>

    {hasGitHubToken ? (
      <div className="mb-6">
        <div className="inline-flex items-center gap-2 px-4 py-2 bg-success-500/20 text-success-400 rounded-full">
          <CheckCircle2 className="w-5 h-5" />
          GitHub Connected
        </div>
      </div>
    ) : (
      <Button
        variant="secondary"
        size="lg"
        onClick={onConnectGitHub}
        icon={Github}
        className="mb-6"
      >
        Connect with GitHub
      </Button>
    )}

    <div className="flex flex-col sm:flex-row gap-3 justify-center">
      <Button variant="ghost" size="md" onClick={onBack} icon={ChevronLeft}>
        Back
      </Button>
      <Button
        variant="gradient"
        size="md"
        onClick={onNext}
        icon={ChevronRight}
        iconPosition="right"
      >
        {hasGitHubToken ? 'Continue' : 'Continue without GitHub'}
      </Button>
      <Button variant="ghost" size="md" onClick={onSkip}>
        Skip
      </Button>
    </div>
  </div>
);

ConnectStep.propTypes = {
  onNext: PropTypes.func.isRequired,
  onBack: PropTypes.func.isRequired,
  onSkip: PropTypes.func.isRequired,
  hasGitHubToken: PropTypes.bool,
  onConnectGitHub: PropTypes.func.isRequired,
};

/**
 * Step: Select Repositories (simplified)
 */
const SelectStep = ({ onNext, onBack, onSkip, onNavigateToImport }) => (
  <div className="text-center">
    <div className="mb-6">
      <div className="inline-flex p-4 rounded-full bg-secondary-500/20 mb-4">
        <GitBranch className="w-12 h-12 text-secondary-400" />
      </div>
      <h2 className="text-2xl font-bold text-white mb-2">
        Select Repositories
      </h2>
      <p className="text-gray-400 max-w-md mx-auto">
        Choose which repositories to analyze. You can add more anytime.
      </p>
    </div>

    {/* Quick Start Info */}
    <div className="bg-primary-500/10 border border-primary-500/20 rounded-lg p-4 mb-6 max-w-md mx-auto">
      <p className="text-primary-300 text-sm">
        <strong>Pro tip:</strong> Start with 5-10 of your best projects.
        You can always import more later.
      </p>
    </div>

    <Button
      variant="gradient"
      size="lg"
      onClick={onNavigateToImport}
      icon={GitBranch}
      className="mb-6"
    >
      Open Repository Selector
    </Button>

    <div className="flex flex-col sm:flex-row gap-3 justify-center">
      <Button variant="ghost" size="md" onClick={onBack} icon={ChevronLeft}>
        Back
      </Button>
      <Button
        variant="outline"
        size="md"
        onClick={onNext}
        icon={ChevronRight}
        iconPosition="right"
      >
        I've imported repos
      </Button>
      <Button variant="ghost" size="md" onClick={onSkip}>
        Skip
      </Button>
    </div>
  </div>
);

SelectStep.propTypes = {
  onNext: PropTypes.func.isRequired,
  onBack: PropTypes.func.isRequired,
  onSkip: PropTypes.func.isRequired,
  onNavigateToImport: PropTypes.func.isRequired,
};

/**
 * Step: Analyzing (Progress)
 */
const AnalyzingStep = ({ onNext, analysisProgress, isAnalyzing }) => (
  <div className="text-center">
    <div className="mb-6">
      <div className="inline-flex p-4 rounded-full bg-warning-500/20 mb-4">
        {isAnalyzing ? (
          <Loader2 className="w-12 h-12 text-warning-400 animate-spin" />
        ) : (
          <Zap className="w-12 h-12 text-warning-400" />
        )}
      </div>
      <h2 className="text-2xl font-bold text-white mb-2">
        {isAnalyzing ? 'Analyzing Your Portfolio' : 'Ready to Analyze'}
      </h2>
      <p className="text-gray-400 max-w-md mx-auto">
        {isAnalyzing
          ? 'Our AI is examining your code patterns, skills, and project relationships.'
          : 'Your repositories are imported. Start the analysis to discover insights.'}
      </p>
    </div>

    {isAnalyzing && (
      <div className="max-w-md mx-auto mb-6">
        {/* Progress Bar */}
        <div className="h-2 bg-white/10 rounded-full overflow-hidden mb-2">
          <div
            className="h-full bg-gradient-to-r from-primary-500 to-secondary-500 transition-all duration-500"
            style={{ width: `${analysisProgress}%` }}
          />
        </div>
        <p className="text-sm text-gray-500">{analysisProgress}% complete</p>

        {/* Analysis Steps */}
        <div className="mt-4 space-y-2 text-left">
          <div className={`flex items-center gap-2 text-sm ${analysisProgress >= 25 ? 'text-success-400' : 'text-gray-500'}`}>
            {analysisProgress >= 25 ? <Check className="w-4 h-4" /> : <Loader2 className="w-4 h-4 animate-spin" />}
            Scanning repository structure
          </div>
          <div className={`flex items-center gap-2 text-sm ${analysisProgress >= 50 ? 'text-success-400' : 'text-gray-500'}`}>
            {analysisProgress >= 50 ? <Check className="w-4 h-4" /> : analysisProgress >= 25 ? <Loader2 className="w-4 h-4 animate-spin" /> : <div className="w-4 h-4" />}
            Identifying technologies & patterns
          </div>
          <div className={`flex items-center gap-2 text-sm ${analysisProgress >= 75 ? 'text-success-400' : 'text-gray-500'}`}>
            {analysisProgress >= 75 ? <Check className="w-4 h-4" /> : analysisProgress >= 50 ? <Loader2 className="w-4 h-4 animate-spin" /> : <div className="w-4 h-4" />}
            Calculating skill proficiencies
          </div>
          <div className={`flex items-center gap-2 text-sm ${analysisProgress >= 100 ? 'text-success-400' : 'text-gray-500'}`}>
            {analysisProgress >= 100 ? <Check className="w-4 h-4" /> : analysisProgress >= 75 ? <Loader2 className="w-4 h-4 animate-spin" /> : <div className="w-4 h-4" />}
            Generating insights
          </div>
        </div>
      </div>
    )}

    {!isAnalyzing && (
      <Button
        variant="gradient"
        size="lg"
        onClick={onNext}
        icon={Zap}
      >
        View Your Insights
      </Button>
    )}
  </div>
);

AnalyzingStep.propTypes = {
  onNext: PropTypes.func.isRequired,
  analysisProgress: PropTypes.number,
  isAnalyzing: PropTypes.bool,
};

/**
 * Step: First Insights
 */
const InsightsStep = ({ onComplete, insights }) => (
  <div className="text-center">
    <div className="mb-6">
      <div className="inline-flex p-4 rounded-full bg-success-500/20 mb-4">
        <BarChart3 className="w-12 h-12 text-success-400" />
      </div>
      <h2 className="text-2xl font-bold text-white mb-2">
        Your Portfolio is Ready!
      </h2>
      <p className="text-gray-400 max-w-md mx-auto">
        We've discovered some initial insights about your work. Explore the dashboard to learn more.
      </p>
    </div>

    {/* Sample Insights */}
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6 max-w-lg mx-auto">
      <div className="p-4 bg-white/5 rounded-lg border border-white/10 text-left">
        <p className="text-2xl font-bold text-primary-400">
          {insights?.topLanguage || 'JavaScript'}
        </p>
        <p className="text-sm text-gray-400">Top Language</p>
      </div>
      <div className="p-4 bg-white/5 rounded-lg border border-white/10 text-left">
        <p className="text-2xl font-bold text-secondary-400">
          {insights?.projectCount || 0}
        </p>
        <p className="text-sm text-gray-400">Projects Analyzed</p>
      </div>
      <div className="p-4 bg-white/5 rounded-lg border border-white/10 text-left">
        <p className="text-2xl font-bold text-success-400">
          {insights?.skillCount || 12}
        </p>
        <p className="text-sm text-gray-400">Skills Identified</p>
      </div>
      <div className="p-4 bg-white/5 rounded-lg border border-white/10 text-left">
        <p className="text-2xl font-bold text-warning-400">
          {insights?.insightCount || 5}
        </p>
        <p className="text-sm text-gray-400">Key Insights</p>
      </div>
    </div>

    <Button
      variant="gradient"
      size="lg"
      onClick={onComplete}
      icon={Sparkles}
    >
      Explore Your Dashboard
    </Button>
  </div>
);

InsightsStep.propTypes = {
  onComplete: PropTypes.func.isRequired,
  insights: PropTypes.shape({
    topLanguage: PropTypes.string,
    projectCount: PropTypes.number,
    skillCount: PropTypes.number,
    insightCount: PropTypes.number,
  }),
};

/**
 * Step: Create Organization (team variant)
 */
const CreateOrgStep = ({ onNext, onBack, onSkip, user }) => {
  const [orgName, setOrgName] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [created, setCreated] = useState(false);

  const handleCreate = async () => {
    if (!orgName.trim()) return;
    setIsCreating(true);
    try {
      await createOrganization(user, { name: orgName.trim() });
      setCreated(true);
      setTimeout(() => onNext(), 600);
    } catch {
      // Allow user to retry or skip
      setIsCreating(false);
    }
  };

  return (
    <div className="text-center">
      <div className="mb-6">
        <div className="inline-flex p-4 rounded-full bg-primary-500/20 mb-4">
          <Building2 className="w-12 h-12 text-primary-400" />
        </div>
        <h2 className="text-2xl font-bold text-white mb-2">
          Create Your Workspace
        </h2>
        <p className="text-gray-400 max-w-md mx-auto">
          Set up a shared space for your team to collaborate on portfolio intelligence.
        </p>
      </div>

      <div className="max-w-md mx-auto mb-6">
        <div className="bg-white/5 rounded-lg border border-white/10 p-6">
          <label className="block text-sm font-medium text-gray-300 mb-2 text-left">
            Workspace Name
          </label>
          <input
            type="text"
            value={orgName}
            onChange={(e) => setOrgName(e.target.value)}
            placeholder="e.g., Acme Corp"
            className="w-full px-4 py-3 bg-white/5 border border-white/20 rounded-lg
                       text-white placeholder-gray-500 focus:outline-none focus:border-primary-500
                       transition-colors"
            disabled={isCreating || created}
          />
          {created && (
            <div className="mt-3 inline-flex items-center gap-2 text-success-400 text-sm">
              <CheckCircle2 className="w-4 h-4" />
              Workspace created
            </div>
          )}
        </div>
      </div>

      <div className="flex flex-col sm:flex-row gap-3 justify-center">
        <Button variant="ghost" size="md" onClick={onBack} icon={ChevronLeft}>
          Back
        </Button>
        <Button
          variant="gradient"
          size="md"
          onClick={handleCreate}
          disabled={!orgName.trim() || isCreating || created}
          icon={isCreating ? Loader2 : ArrowRight}
          iconPosition="right"
        >
          {isCreating ? 'Creating...' : 'Create Workspace'}
        </Button>
        <Button variant="ghost" size="md" onClick={onSkip}>
          Skip
        </Button>
      </div>
    </div>
  );
};

CreateOrgStep.propTypes = {
  onNext: PropTypes.func.isRequired,
  onBack: PropTypes.func.isRequired,
  onSkip: PropTypes.func.isRequired,
  user: PropTypes.object,
};

/**
 * Step: Invite Team Members (team variant)
 */
const InviteTeamStep = ({ onNext, onBack, onSkip }) => {
  const [email, setEmail] = useState('');
  const [invited, setInvited] = useState([]);

  const handleInvite = () => {
    if (!email.trim() || !email.includes('@')) return;
    setInvited((prev) => [...prev, email.trim()]);
    setEmail('');
  };

  return (
    <div className="text-center">
      <div className="mb-6">
        <div className="inline-flex p-4 rounded-full bg-secondary-500/20 mb-4">
          <Users className="w-12 h-12 text-secondary-400" />
        </div>
        <h2 className="text-2xl font-bold text-white mb-2">
          Invite Your Team
        </h2>
        <p className="text-gray-400 max-w-md mx-auto">
          Add team members to collaborate on portfolio analysis. You can always invite more later.
        </p>
      </div>

      <div className="max-w-md mx-auto mb-6">
        <div className="bg-white/5 rounded-lg border border-white/10 p-6">
          <div className="flex gap-2">
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleInvite()}
              placeholder="colleague@company.com"
              className="flex-1 px-4 py-3 bg-white/5 border border-white/20 rounded-lg
                         text-white placeholder-gray-500 focus:outline-none focus:border-primary-500
                         transition-colors"
            />
            <Button variant="secondary" size="md" onClick={handleInvite} icon={Mail}>
              Send
            </Button>
          </div>

          {invited.length > 0 && (
            <div className="mt-4 space-y-2 text-left">
              {invited.map((addr) => (
                <div
                  key={addr}
                  className="flex items-center gap-2 text-sm text-success-400"
                >
                  <CheckCircle2 className="w-4 h-4 flex-shrink-0" />
                  {addr}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="flex flex-col sm:flex-row gap-3 justify-center">
        <Button variant="ghost" size="md" onClick={onBack} icon={ChevronLeft}>
          Back
        </Button>
        <Button
          variant="gradient"
          size="md"
          onClick={onNext}
          icon={ChevronRight}
          iconPosition="right"
        >
          Continue
        </Button>
        <Button variant="ghost" size="md" onClick={onSkip}>
          Skip
        </Button>
      </div>
    </div>
  );
};

InviteTeamStep.propTypes = {
  onNext: PropTypes.func.isRequired,
  onBack: PropTypes.func.isRequired,
  onSkip: PropTypes.func.isRequired,
};

/**
 * Step: Enterprise Context (enterprise variant)
 */
const EnterpriseContextStep = ({ onNext, onBack, onSkip, user }) => {
  const [mode, setMode] = useState(null); // 'create' | 'invited'
  const [orgName, setOrgName] = useState('');
  const [inviteToken, setInviteToken] = useState('');
  const [isCreating, setIsCreating] = useState(false);

  const handleCreateOrg = async () => {
    if (!orgName.trim()) return;
    setIsCreating(true);
    try {
      await createOrganization(user, { name: orgName.trim() });
      setTimeout(() => onNext(), 400);
    } catch {
      setIsCreating(false);
    }
  };

  const handleJoinWithToken = () => {
    if (!inviteToken.trim()) return;
    // Token validation would happen server-side; advance for now
    onNext();
  };

  return (
    <div className="text-center">
      <div className="mb-6">
        <div className="inline-flex p-4 rounded-full bg-primary-500/20 mb-4">
          <Building2 className="w-12 h-12 text-primary-400" />
        </div>
        <h2 className="text-2xl font-bold text-white mb-2">
          Set Up Your Workspace
        </h2>
        <p className="text-gray-400 max-w-md mx-auto">
          How would you like to get started?
        </p>
      </div>

      {!mode && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-lg mx-auto mb-6">
          <button
            onClick={() => setMode('create')}
            className="p-6 bg-white/5 rounded-lg border border-white/10 hover:border-primary-500/50
                       hover:bg-primary-500/5 transition-all text-left group"
          >
            <Building2 className="w-8 h-8 text-primary-400 mb-3 group-hover:scale-110 transition-transform" />
            <h3 className="text-white font-semibold mb-1">Create a Workspace</h3>
            <p className="text-sm text-gray-400">Start fresh with a new workspace</p>
          </button>
          <button
            onClick={() => setMode('invited')}
            className="p-6 bg-white/5 rounded-lg border border-white/10 hover:border-secondary-500/50
                       hover:bg-secondary-500/5 transition-all text-left group"
          >
            <Clipboard className="w-8 h-8 text-secondary-400 mb-3 group-hover:scale-110 transition-transform" />
            <h3 className="text-white font-semibold mb-1">I was invited</h3>
            <p className="text-sm text-gray-400">Join with an invite token</p>
          </button>
        </div>
      )}

      {mode === 'create' && (
        <div className="max-w-md mx-auto mb-6">
          <div className="bg-white/5 rounded-lg border border-white/10 p-6">
            <label className="block text-sm font-medium text-gray-300 mb-2 text-left">
              Workspace Name
            </label>
            <input
              type="text"
              value={orgName}
              onChange={(e) => setOrgName(e.target.value)}
              placeholder="e.g., Acme Corp"
              className="w-full px-4 py-3 bg-white/5 border border-white/20 rounded-lg
                         text-white placeholder-gray-500 focus:outline-none focus:border-primary-500
                         transition-colors mb-4"
              disabled={isCreating}
            />
            <Button
              variant="gradient"
              size="md"
              onClick={handleCreateOrg}
              disabled={!orgName.trim() || isCreating}
              icon={isCreating ? Loader2 : ArrowRight}
              iconPosition="right"
              className="w-full"
            >
              {isCreating ? 'Creating...' : 'Create & Continue'}
            </Button>
          </div>
        </div>
      )}

      {mode === 'invited' && (
        <div className="max-w-md mx-auto mb-6">
          <div className="bg-white/5 rounded-lg border border-white/10 p-6">
            <label className="block text-sm font-medium text-gray-300 mb-2 text-left">
              Invite Token
            </label>
            <input
              type="text"
              value={inviteToken}
              onChange={(e) => setInviteToken(e.target.value)}
              placeholder="Paste your invite token"
              className="w-full px-4 py-3 bg-white/5 border border-white/20 rounded-lg
                         text-white placeholder-gray-500 focus:outline-none focus:border-primary-500
                         transition-colors mb-4"
            />
            <Button
              variant="gradient"
              size="md"
              onClick={handleJoinWithToken}
              disabled={!inviteToken.trim()}
              icon={ArrowRight}
              iconPosition="right"
              className="w-full"
            >
              Join Workspace
            </Button>
          </div>
        </div>
      )}

      <div className="flex flex-col sm:flex-row gap-3 justify-center">
        <Button variant="ghost" size="md" onClick={mode ? () => setMode(null) : onBack} icon={ChevronLeft}>
          Back
        </Button>
        <button
          onClick={onSkip}
          className="text-sm text-gray-500 hover:text-gray-300 transition-colors"
        >
          Just explore for now
        </button>
      </div>
    </div>
  );
};

EnterpriseContextStep.propTypes = {
  onNext: PropTypes.func.isRequired,
  onBack: PropTypes.func.isRequired,
  onSkip: PropTypes.func.isRequired,
  user: PropTypes.object,
};

/**
 * Step: Ready (generic completion for team/enterprise/developer variants)
 */
const ReadyStep = ({ onComplete, persona }) => {
  const guidance = persona ? PERSONA_GUIDANCE[persona] : null;

  return (
    <div className="text-center">
      <div className="mb-6">
        <div className="inline-flex p-4 rounded-full bg-success-500/20 mb-4">
          <CheckCircle2 className="w-12 h-12 text-success-400" />
        </div>
        <h2 className="text-2xl font-bold text-white mb-2">
          You're All Set!
        </h2>
        <p className="text-gray-400 max-w-md mx-auto">
          {guidance
            ? guidance.valueProp
            : 'Your workspace is ready. Explore the dashboard to get started.'}
        </p>
      </div>

      <Button
        variant="gradient"
        size="lg"
        onClick={onComplete}
        icon={Sparkles}
      >
        Go to Dashboard
      </Button>
    </div>
  );
};

ReadyStep.propTypes = {
  onComplete: PropTypes.func.isRequired,
  persona: PropTypes.string,
};

/**
 * Main OnboardingWizard Component
 */
const OnboardingWizard = ({
  isOpen,
  onComplete,
  onSkip,
  hasGitHubToken = false,
  onConnectGitHub,
  onNavigateToImport,
  onNavigateToValue,
  onNavigateToTab,
  insights,
  user,
}) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [activeVariant, setActiveVariant] = useState('individual');
  const [analysisProgress, setAnalysisProgress] = useState(0);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [selectedPersona, setSelectedPersona] = useState(null);
  const dialogRef = useRef(null);
  const previousActiveElement = useRef(null);
  const hasTrackedStart = useRef(false);
  const { trackEvent, events } = useAnalytics();
  const { switchPersona, persona: currentPersona } = usePersona();

  // Quick-scan: infer persona from GitHub as soon as token is available
  const githubToken = hasGitHubToken ? (getGitHubToken() || null) : null;
  const {
    inferredPersona,
    confidence: inferenceConfidence,
    isInferring,
  } = usePersonaInference(githubToken);

  // Derive steps from active variant
  const steps = useMemo(
    () => VARIANT_STEPS[activeVariant] || VARIANT_STEPS.individual,
    [activeVariant]
  );

  // Current step id for rendering
  const currentStepId = steps[currentStep]?.id;

  // Persona-specific guidance for the welcome step
  const personaGuidance = useMemo(() => {
    const p = selectedPersona || currentPersona;
    return p ? PERSONA_GUIDANCE[p] || null : null;
  }, [selectedPersona, currentPersona]);

  /**
   * Track onboarding started when wizard opens
   */
  // Pre-select persona from landing page CTA
  useEffect(() => {
    if (isOpen) {
      const preselected = sessionStorage.getItem('preselectedPersona');
      if (preselected && getPersonaConfig(preselected)) {
        setSelectedPersona(preselected);
        sessionStorage.removeItem('preselectedPersona');
      }
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && !hasTrackedStart.current) {
      trackEvent(events.ONBOARDING_STARTED, { step: steps[0].id });
      hasTrackedStart.current = true;
    }
  }, [isOpen, trackEvent, events, steps]);

  /**
   * Track step changes
   */
  useEffect(() => {
    if (isOpen && currentStep > 0 && currentStep < steps.length) {
      trackEvent(events.ONBOARDING_STEP_COMPLETED, {
        step: steps[currentStep - 1].id,
        stepIndex: currentStep - 1,
        nextStep: steps[currentStep].id,
      });
    }
  }, [currentStep, isOpen, trackEvent, events, steps]);

  /**
   * Handle escape key to skip/close
   */
  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === 'Escape') {
        onSkip();
      }
    },
    [onSkip]
  );

  /**
   * Focus management
   */
  useEffect(() => {
    if (isOpen) {
      previousActiveElement.current = document.activeElement;
      dialogRef.current?.focus();
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';

      return () => {
        document.removeEventListener('keydown', handleKeyDown);
        document.body.style.overflow = '';
        if (previousActiveElement.current) {
          previousActiveElement.current.focus();
        }
      };
    }
  }, [isOpen, handleKeyDown]);

  /**
   * Simulate analysis progress (only for analyzing step)
   */
  useEffect(() => {
    if (currentStepId === 'analyzing' && isAnalyzing) {
      const interval = setInterval(() => {
        setAnalysisProgress((prev) => {
          if (prev >= 100) {
            clearInterval(interval);
            setIsAnalyzing(false);
            // Auto-advance to insights
            const insightsIndex = steps.findIndex((s) => s.id === 'insights');
            if (insightsIndex !== -1) {
              setTimeout(() => setCurrentStep(insightsIndex), 500);
            }
            return 100;
          }
          return prev + 5;
        });
      }, 200);

      return () => clearInterval(interval);
    }
  }, [currentStepId, isAnalyzing, steps]);

  const handlePersonaSelect = (personaId) => {
    setSelectedPersona(personaId);
    switchPersona(personaId);
    trackEvent(events.PERSONA_SELECTED, { persona: personaId });
    trackEvent(events.PERSONA_ONBOARDING_STARTED, { persona: personaId });

    // Derive variant from persona config
    const config = getPersonaConfig(personaId);
    const variant = config?.onboardingVariant || 'individual';
    setActiveVariant(variant);

    // Advance past the persona step dynamically — find persona index in the
    // new variant's steps and jump to the step after it
    const newSteps = VARIANT_STEPS[variant] || VARIANT_STEPS.individual;
    const personaIndex = newSteps.findIndex(s => s.id === 'persona');
    setCurrentStep(personaIndex !== -1 ? personaIndex + 1 : currentStep + 1);
  };

  const handlePersonaSkip = () => {
    // Default persona already set (professional), keep individual variant
    setCurrentStep(currentStep + 1);
  };

  const handleNext = () => {
    if (currentStepId === 'analyzing') {
      // Start analysis simulation
      setIsAnalyzing(true);
      setAnalysisProgress(0);
    } else if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSkip = () => {
    trackEvent(events.ONBOARDING_SKIPPED, {
      skippedAtStep: steps[currentStep].id,
      stepIndex: currentStep,
      totalSteps: steps.length,
    });
    onSkip();
  };

  const handleStepSkip = () => {
    // Skip the current step (advance without completing action)
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      handleSkip();
    }
  };

  const handleComplete = () => {
    trackEvent(events.ONBOARDING_COMPLETED, {
      totalSteps: steps.length,
      persona: selectedPersona,
      variant: activeVariant,
      insights: insights ? {
        projectCount: insights.projectCount,
        skillCount: insights.skillCount,
      } : null,
    });

    if (selectedPersona) {
      trackEvent(events.PERSONA_ONBOARDING_COMPLETED, {
        persona: selectedPersona,
      });
    }

    const personaConfig = selectedPersona ? getPersonaConfig(selectedPersona) : null;
    onComplete({
      persona: selectedPersona,
      variant: activeVariant,
      defaultTab: personaConfig?.defaultTab,
    });
  };

  const handleNavigateToImport = () => {
    onSkip(); // Close wizard
    if (onNavigateToImport) {
      onNavigateToImport();
    }
  };

  if (!isOpen) return null;

  /**
   * Render step content by current step id
   */
  const renderStepContent = () => {
    switch (currentStepId) {
      case 'welcome':
        return (
          <WelcomeStep
            onNext={handleNext}
            onSkip={handleSkip}
            personaGuidance={personaGuidance}
          />
        );
      case 'persona':
        return (
          <PersonaSelectionStep
            onSelect={handlePersonaSelect}
            onSkip={handlePersonaSkip}
            onBack={handleBack}
            preselected={selectedPersona || currentPersona}
            suggestedPersona={inferredPersona}
            suggestionConfidence={inferenceConfidence}
            isInferring={isInferring}
          />
        );
      case 'connect':
        return (
          <ConnectStep
            onNext={handleNext}
            onBack={handleBack}
            onSkip={handleStepSkip}
            hasGitHubToken={hasGitHubToken}
            onConnectGitHub={onConnectGitHub}
          />
        );
      case 'select':
        return (
          <SelectStep
            onNext={handleNext}
            onBack={handleBack}
            onSkip={handleStepSkip}
            onNavigateToImport={handleNavigateToImport}
          />
        );
      case 'analyzing':
        return (
          <AnalyzingStep
            onNext={handleNext}
            analysisProgress={analysisProgress}
            isAnalyzing={isAnalyzing}
          />
        );
      case 'insights':
        return (
          <InsightsStep
            onComplete={handleComplete}
            insights={insights}
          />
        );
      case 'create-org':
        return (
          <CreateOrgStep
            onNext={handleNext}
            onBack={handleBack}
            onSkip={handleStepSkip}
            user={user}
          />
        );
      case 'invite-team':
        return (
          <InviteTeamStep
            onNext={handleNext}
            onBack={handleBack}
            onSkip={handleStepSkip}
          />
        );
      case 'enterprise-context':
        return (
          <EnterpriseContextStep
            onNext={handleNext}
            onBack={handleBack}
            onSkip={handleStepSkip}
            user={user}
          />
        );
      case 'ready':
        return (
          <ReadyStep
            onComplete={handleComplete}
            persona={selectedPersona}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="onboarding-title"
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/80 backdrop-blur-md"
        aria-hidden="true"
      />

      {/* Modal */}
      <div
        ref={dialogRef}
        tabIndex={-1}
        className="relative bg-slate-900 border border-white/10 rounded-2xl shadow-2xl
                   w-full max-w-2xl max-h-[90vh] overflow-y-auto
                   transform transition-all duration-300 ease-out"
      >
        {/* Skip button */}
        <button
          onClick={handleSkip}
          className="absolute top-4 right-4 p-2 rounded-lg text-gray-400
                     hover:text-white hover:bg-white/10 transition-colors z-10"
          aria-label="Skip onboarding"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Content */}
        <div className="p-6 sm:p-8">
          {/* Progress Stepper */}
          <ProgressStepper currentStep={currentStep} steps={steps} />

          {/* Step Content */}
          <div className="min-h-[300px]">
            {renderStepContent()}
          </div>
        </div>
      </div>
    </div>
  );
};

OnboardingWizard.propTypes = {
  /** Whether the wizard is open */
  isOpen: PropTypes.bool.isRequired,
  /** Callback when onboarding is completed */
  onComplete: PropTypes.func.isRequired,
  /** Callback when onboarding is skipped */
  onSkip: PropTypes.func.isRequired,
  /** Whether user has connected GitHub */
  hasGitHubToken: PropTypes.bool,
  /** Callback to trigger GitHub OAuth */
  onConnectGitHub: PropTypes.func.isRequired,
  /** Callback to navigate to import page */
  onNavigateToImport: PropTypes.func,
  /** Callback to navigate to Value Intelligence */
  onNavigateToValue: PropTypes.func,
  /** Callback to navigate to a specific tab (receives tab id string) */
  onNavigateToTab: PropTypes.func,
  /** Initial insights data to display */
  insights: PropTypes.shape({
    topLanguage: PropTypes.string,
    projectCount: PropTypes.number,
    skillCount: PropTypes.number,
    insightCount: PropTypes.number,
  }),
  /** Current authenticated user object (needed for org creation) */
  user: PropTypes.object,
};

export default OnboardingWizard;
